/**
 * ============================================================================
 * FormulaHelpGuide.jsx
 * ============================================================================
 *
 * Componente de Ajuda para o Sistema de Fórmulas
 *
 * Exibe:
 * - Variáveis disponíveis
 * - Funções matemáticas
 * - Operadores
 * - Períodos de tempo
 * - Exemplos práticos
 * - Dicas importantes
 *
 * ============================================================================
 */

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faQuestionCircle,
  faCalculator,
  faClock,
  faBoxes,
  faIndustry,
  faChartLine,
  faExclamationTriangle,
  faChevronDown,
  faChevronUp,
  faCopy,
} from '@fortawesome/free-solid-svg-icons';

const FormulaHelpGuide = ({ onVariableClick }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [copiedVariable, setCopiedVariable] = useState(null);
  const [expandedExample, setExpandedExample] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleVariableClick = (variableName) => {
    // Se callback foi passado, chamar ele
    if (onVariableClick) {
      onVariableClick(variableName);
    }

    // Mostrar feedback visual
    setCopiedVariable(variableName);
    setTimeout(() => setCopiedVariable(null), 1500);
  };

  // ========================================================================
  // DADOS
  // ========================================================================

  const variableGroups = [
    {
      title: 'Stock',
      icon: faBoxes,
      color: 'primary',
      id: 'stock',
      variables: [
        { name: 'stock-atual', description: 'Stock atual em kg' },
        { name: 'stock-minimo', description: 'Stock mínimo configurado' },
        { name: 'stock-maximo', description: 'Stock máximo configurado' },
        { name: 'stock-disponivel', description: 'Stock disponível (atual - reservado)' },
        { name: 'stock-reservado', description: 'Stock reservado para encomendas' },
      ],
    },
    {
      title: 'Encomendas',
      icon: faChartLine,
      color: 'success',
      id: 'orders',
      variables: [
        { name: 'encomendas_15d', description: 'Encomendas para os próximos 15 dias' },
        { name: 'encomendas_30d', description: 'Encomendas para os próximos 30 dias' },
        { name: 'encomendas_60d', description: 'Encomendas para os próximos 60 dias' },
        { name: 'encomendas_3m', description: 'Encomendas para os próximos 3 meses' },
        { name: 'encomendas_1a', description: 'Encomendas para o próximo ano' },
        { name: 'encomendas_YYYYMMDD-YYYYMMDD', description: 'Encomendas em período específico' },
      ],
    },
    {
      title: 'Histórico de vendas',
      icon: faChartLine,
      color: 'info',
      id: 'history',
      variables: [
        { name: 'historico-venda_30d', description: 'Soma de vendas dos últimos 30 dias' },
        { name: 'historico-venda_60d', description: 'Soma de vendas dos últimos 60 dias' },
        { name: 'historico-venda_20250301-20250308', description: 'Vendas em período específico (ex: Carnaval)' },
        { name: 'historico-semanal_8s', description: 'Média semanal das últimas 8 semanas' },
        { name: 'historico-mensal_6m', description: 'Média mensal dos últimos 6 meses' },
      ],
    },
    {
      title: 'Produção',
      icon: faIndustry,
      color: 'warning',
      id: 'production',
      variables: [
        { name: 'em-producao', description: 'Quantidade em produção atual' },
        { name: 'em-cura', description: 'Quantidade em processo de cura' },
        { name: 'tempo-cura', description: 'Tempo de cura em dias do produto' },
        { name: 'producao-planejada_30d', description: 'Produção planejada próximos 30 dias' },
        { name: 'producao-concluida_30d', description: 'Produção que será concluída em 30 dias' },
      ],
    },
  ];

  const functions = [
    {
      name: 'MAX(a, b)',
      description: 'Valor máximo entre a e b',
      example: 'MAX(stock-minimo, encomendas_30d) - Garante mínimo',
    },
    {
      name: 'MIN(a, b)',
      description: 'Valor mínimo entre a e b',
      example: 'MIN(stock-maximo, encomendas_30d + 100)',
    },
    {
      name: 'IF(condição, verdadeiro, falso)',
      description: 'Condicional: se cond então V senão F',
      example: 'IF(stock-atual <= stock-minimo, encomendas_30d * 1.5, 0)',
    },
    {
      name: 'ABS(valor)',
      description: 'Valor absoluto (remove negativos)',
      example: 'ABS(encomendas_30d - 100)',
    },
    {
      name: 'ROUND(valor, casas)',
      description: 'Arredonda para número de casas decimais',
      example: 'ROUND(historico-semanal_8s * 4.5, 0)',
    },
  ];

  const operators = [
    {
      symbol: '+, -, *, /',
      description: 'Operações matemáticas básicas',
    },
    {
      symbol: '>, <, >=, <=',
      description: 'Comparações numéricas (para IF)',
    },
    {
      symbol: '==, !=',
      description: 'Igualdade e diferença',
    },
    {
      symbol: '( )',
      description: 'Agrupamento e precedência de operações',
    },
  ];

  const timePeriods = {
    relative: [
      { code: 'd', description: 'dias (ex: 30d = 30 dias)' },
      { code: 's', description: 'semanas (ex: 8s = 8 semanas)' },
      { code: 'm', description: 'meses (ex: 3m = 3 meses)' },
      { code: 'a', description: 'anos (ex: 1a = 1 ano)' },
    ],
    specific: [
      {
        format: 'YYYYMMDD-YYYYMMDD',
        example: '20250301-20250308',
        description: 'Período específico (Carnaval)',
      },
      {
        format: 'YYYYMMDD-YYYYMMDD',
        example: '20250101-20250131',
        description: 'Período específico (Janeiro)',
      },
    ],
  };

  const examples = [
    // Exemplos por Variável
    {
      title: 'Stock: Usar Stock Atual',
      formula: 'stock-minimo - stock-atual',
      explanation: 'Produzir a diferença entre mínimo e atual. Se resultado for negativo, não precisa produzir.',
      difficulty: 'Básico',
      category: 'Stock',
    },
    {
      title: 'Stock: Considerar Stock Disponível',
      formula: 'stock-minimo + encomendas_30d - stock-disponivel',
      explanation: 'Usa stock-disponivel em vez de stock-atual, ignorando reservas para encomendas.',
      difficulty: 'Básico',
      category: 'Stock',
    },
    {
      title: 'Encomendas: Baseado em 30 Dias',
      formula: 'MAX(stock-minimo, encomendas_30d) - stock-atual',
      explanation: 'Produzir o máximo entre o estoque mínimo e encomendas dos últimos 30 dias.',
      difficulty: 'Básico',
      category: 'Encomendas',
    },
    {
      title: 'Encomendas: Comparar Períodos',
      formula: 'MAX(encomendas_15d, encomendas_30d) * 1.1 - stock-atual',
      explanation: 'Usa o período com maior demanda e adiciona 10% de margem.',
      difficulty: 'Intermediário',
      category: 'Encomendas',
    },
    {
      title: 'Histórico: Vendas Passadas',
      formula: 'historico-venda_30d * 1.5 - stock-atual',
      explanation: 'Produzir 1.5x o total de vendas dos últimos 30 dias.',
      difficulty: 'Básico',
      category: 'Histórico',
    },
    {
      title: 'Histórico: Média Semanal',
      formula: 'historico-semanal_8s * 4 + stock-minimo - stock-atual',
      explanation: 'Produzir 4 semanas baseado na média semanal das últimas 8 semanas.',
      difficulty: 'Intermediário',
      category: 'Histórico',
    },
    {
      title: 'Produção: Considerar Itens em Cura',
      formula: 'encomendas_30d + stock-minimo - stock-atual - em-cura',
      explanation: 'Desconta da produção sugerida o que já está em cura (produção em andamento).',
      difficulty: 'Básico',
      category: 'Produção',
    },
    {
      title: 'Produção: Com Tempo de Cura',
      formula: 'IF(tempo-cura > 15, encomendas_30d * 1.3, encomendas_30d) - stock-atual',
      explanation: 'Se cura > 15 dias, aumenta 30% a produção para antecipar. Caso contrário, quantidade normal.',
      difficulty: 'Intermediário',
      category: 'Produção',
    },
    // Fórmulas Combinadas
    {
      title: 'Fórmula Básica',
      formula: 'stock-minimo + encomendas_30d - stock-atual',
      explanation: 'Produzir para manter estoque mínimo + demanda dos últimos 30 dias',
      difficulty: 'Básico',
      category: 'Combinada',
    },
    {
      title: 'Com Segurança (20% de margem)',
      formula: 'MAX(stock-minimo, encomendas_30d * 1.2) - stock-atual',
      explanation:
        'Garante estoque mínimo e adiciona 20% de margem na demanda para maior segurança',
      difficulty: 'Intermediário',
      category: 'Combinada',
    },
    {
      title: 'Baseada em Histórico',
      formula: 'historico-venda_60d + stock-minimo - stock-atual - em-cura',
      explanation:
        'Baseia-se no total de vendas dos últimos 60 dias, considera produção em andamento',
      difficulty: 'Intermediário',
      category: 'Combinada',
    },
    {
      title: 'Condicional Simples',
      formula:
        'IF(stock-atual <= stock-minimo, encomendas_30d * 1.5 + stock-minimo, historico-mensal_3m) - stock-atual',
      explanation:
        'Se estoque baixo → produção URGENTE; Senão → baseado em média mensal',
      difficulty: 'Avançado',
      category: 'Combinada',
    },
    {
      title: 'Sazonal (Carnaval)',
      formula:
        'IF(historico-venda_20250301-20250308 > 100, historico-venda_20250301-20250308 * 2 + stock-minimo - stock-atual, historico-mensal_3m - stock-atual)',
      explanation:
        'Se vendeu > 100kg no Carnaval passado → produzir 2x. Caso contrário, usa média mensal',
      difficulty: 'Avançado',
      category: 'Combinada',
    },
  ];

  const tips = [
    {
      icon: 'check',
      title: 'Use MAX para evitar números negativos',
      description: 'MAX(0, sua_formula) garante que o resultado seja sempre ≥ 0',
    },
    {
      icon: 'lightbulb',
      title: 'Sempre considere produção em andamento',
      description: 'Subtraia "em-cura" de sua fórmula para não contar produção já iniciada',
    },
    {
      icon: 'hourglass',
      title: 'Teste antes de ativar',
      description:
        'Use o modal de teste com dados reais antes de ativar uma nova fórmula em produção',
    },
    {
      icon: 'chart-line',
      title: 'Escolha períodos apropriados',
      description:
        'Produtos com alta demanda: períodos curtos (15-30d). Produtos sazonais: períodos longos (60-90d)',
    },
    {
      icon: 'lock',
      title: 'Apenas uma fórmula ativa',
      description: 'O sistema sempre usa a fórmula marcada como ativa. Histórico é mantido para todas.',
    },
    {
      icon: 'copy',
      title: 'Use exemplos como base',
      description:
        'Comece com uma fórmula exemplo e customize conforme suas necessidades específicas',
    },
  ];

  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <div className="container-fluid formula-help-guide">
      {/* Header */}
      <div className="mb-4">
        <h2 className="h4 mb-2">
          <FontAwesomeIcon icon={faQuestionCircle} className="me-2 text-info" />
          Guia de Fórmulas de Produção
        </h2>
        <p className="text-muted">
          Aprenda como criar e usar fórmulas para calcular sugestões de produção
        </p>
      </div>

      {/* Introduction */}
      <div className="alert alert-info mb-4">
        <h6 className="mb-2">
          <FontAwesomeIcon icon={faCalculator} className="me-2" />
          Como Funcionam as Fórmulas
        </h6>
        <p className="mb-0">
          As fórmulas usam uma linguagem simplificada que converte automaticamente suas
          palavras-chave em consultas ao banco de dados. Use <strong>hífen (-)</strong> para
          palavras compostas e <strong>underscore (_)</strong> com números para indicar períodos
          de tempo.
        </p>
      </div>

      {/* Variables Section */}
      <div className="mb-4">
        <h5 className="mb-3">
          <FontAwesomeIcon icon={faBoxes} className="me-2 text-primary" />
          Variáveis Disponíveis
        </h5>
        <p className="text-muted small mb-3">
          💡 Clique em qualquer variável para inseri-la na fórmula
        </p>
        <div className="row">
          {variableGroups.map((group) => (
            <div key={group.id} className="col-lg-6 mb-3">
              <div className="card h-100">
                <div className="card-header">
                  <h6 className={`mb-0 text-${group.color}`}>
                    <FontAwesomeIcon icon={group.icon} className="me-2" />
                    {group.title}
                  </h6>
                </div>
                <div className="card-body">
                  {group.variables.map((variable, idx) => (
                    <div
                      key={idx}
                      className="mb-2 p-2 rounded formula-variable-item"
                      onClick={() => handleVariableClick(variable.name)}
                      style={{
                        backgroundColor: copiedVariable === variable.name ? '#d4edda' : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: copiedVariable === variable.name ? '2px solid #28a745' : '1px solid transparent',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                      title="Clique para inserir na fórmula"
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleVariableClick(variable.name);
                        }
                      }}
                    >
                      {/* Highlight animation */}
                      {copiedVariable === variable.name && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(40, 167, 69, 0.1)',
                            animation: 'pulse 0.6s ease-out',
                            pointerEvents: 'none',
                          }}
                        />
                      )}

                      <code className={`text-${group.color} d-flex justify-content-between align-items-center position-relative`}>
                        <span>{variable.name}</span>
                        <FontAwesomeIcon
                          icon={faCopy}
                          className="ms-2"
                          style={{
                            fontSize: '0.75rem',
                            opacity: copiedVariable === variable.name ? 1 : 0.5,
                            transition: 'opacity 0.3s ease',
                          }}
                        />
                      </code>
                      <small className="text-muted d-block">{variable.description}</small>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Functions and Operators */}
      <div className="row mb-4">
        {/* Functions */}
        <div className="col-lg-6 mb-3">
          <h5 className="mb-3">
            <FontAwesomeIcon icon={faCalculator} className="me-2 text-success" />
            Funções Matemáticas
          </h5>
          <div className="card">
            <div className="card-body">
              {functions.map((func, idx) => (
                <div key={idx} className="mb-3 pb-3 border-bottom last:border-0">
                  <code className="text-success d-block mb-1">{func.name}</code>
                  <small className="text-muted d-block">{func.description}</small>
                  <small className="text-secondary d-block mt-1">
                    <strong>Exemplo:</strong> {func.example}
                  </small>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Operators */}
        <div className="col-lg-6 mb-3">
          <h5 className="mb-3">
            <FontAwesomeIcon icon={faCalculator} className="me-2 text-warning" />
            Operadores
          </h5>
          <div className="card">
            <div className="card-body">
              {operators.map((op, idx) => (
                <div key={idx} className="mb-2">
                  <code className="text-warning d-block">{op.symbol}</code>
                  <small className="text-muted d-block">{op.description}</small>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Time Periods */}
      <div className="mb-4">
        <h5 className="mb-3">
          <FontAwesomeIcon icon={faClock} className="me-2 text-secondary" />
          Períodos de Tempo
        </h5>
        <div className="card">
          <div className="card-body">
            <div className="row">
              {/* Relative Periods */}
              <div className="col-md-6 mb-3">
                <h6 className="text-primary mb-2">Períodos Relativos</h6>
                <ul className="list-unstyled">
                  {timePeriods.relative.map((period, idx) => (
                    <li key={idx} className="mb-1">
                      <code className="text-primary">_{'{numero}'}{period.code}</code> -{' '}
                      <small className="text-muted">{period.description}</small>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Specific Periods */}
              <div className="col-md-6">
                <h6 className="text-primary mb-2">Períodos Específicos</h6>
                <ul className="list-unstyled">
                  {timePeriods.specific.map((period, idx) => (
                    <li key={idx} className="mb-2">
                      <code className="text-primary">_{period.example}</code>
                      <small className="d-block text-muted mt-1">{period.description}</small>
                    </li>
                  ))}
                </ul>
                <small className="text-muted d-block mt-2">
                  <strong>Formato:</strong> YYYYMMDD (ex: 20250325 = 25/03/2025)
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Examples */}
      <div className="mb-4">
        <h5 className="mb-3">
          <FontAwesomeIcon icon={faChartLine} className="me-2 text-info" />
          Exemplos Práticos
        </h5>
        <p className="text-muted small mb-3">
          💡 Explore exemplos para cada tipo de variável e veja como combiná-las em fórmulas
        </p>
        <div className="space-y-2">
          {examples.map((example, idx) => {
            const categoryColors = {
              'Stock': 'primary',
              'Encomendas': 'success',
              'Histórico': 'info',
              'Produção': 'warning',
              'Combinada': 'secondary',
            };
            const categoryColor = categoryColors[example.category] || 'secondary';
            const isExpanded = expandedExample === idx;

            return (
              <div key={idx} className="card">
                <button
                  className="card-header bg-white p-3 text-left cursor-pointer hover:bg-light transition-colors d-flex align-items-center justify-content-between"
                  onClick={() => setExpandedExample(isExpanded ? null : idx)}
                  style={{ border: 'none', textDecoration: 'none' }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span className={`badge bg-${categoryColor}`}>{example.category || example.difficulty}</span>
                    <span className={`badge bg-${example.difficulty === 'Básico' ? 'success' : example.difficulty === 'Intermediário' ? 'warning' : 'danger'}`}>
                      {example.difficulty}
                    </span>
                    <span className="text-dark fw-500">{example.title}</span>
                  </div>
                  <FontAwesomeIcon
                    icon={isExpanded ? faChevronUp : faChevronDown}
                    className="text-muted"
                  />
                </button>
                {isExpanded && (
                  <div className="card-body bg-light">
                    <div className="mb-3">
                      <strong>Fórmula:</strong>
                      <pre className="bg-white p-3 mt-2 rounded border">
                        <code className="text-info">{example.formula}</code>
                      </pre>
                    </div>
                    <div className="bg-white p-3 rounded border">
                      <strong>Explicação:</strong>
                      <p className="mb-0 text-muted mt-2">{example.explanation}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Tips */}
      <div className="mb-4">
        <h5 className="mb-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
          Dicas Importantes
        </h5>
        <div className="row">
          {tips.map((tip, idx) => (
            <div key={idx} className="col-md-6 mb-3">
              <div className="card h-100 border-left border-3 border-warning">
                <div className="card-body">
                  <h6 className="card-title text-warning mb-2">
                    {tip.title}
                  </h6>
                  <p className="card-text small text-muted mb-0">{tip.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Best Practices */}
      <div className="alert alert-success">
        <h6 className="mb-2">
          <FontAwesomeIcon icon={faChartLine} className="me-2" />
          Boas Práticas
        </h6>
        <ul className="mb-0">
          <li>Comece com fórmulas simples e vá aumentando a complexidade</li>
          <li>Sempre envolva cálculos que podem ser negativos com MAX(0, ...)</li>
          <li>Teste sua fórmula com dados reais antes de ativar</li>
          <li>Considere períodos de tempo apropriados para seu tipo de produto</li>
          <li>Mantenha um histórico das fórmulas antigas para referência futura</li>
          <li>Documente o propósito de cada fórmula na descrição</li>
        </ul>
      </div>

      {/* Estilos CSS para animações */}
      <style>{`
        @keyframes pulse {
          0% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.05);
          }
          100% {
            opacity: 0;
            transform: scale(1);
          }
        }

        .formula-variable-item {
          transition: all 0.3s ease;
        }

        .formula-variable-item:hover {
          background-color: rgba(0, 123, 255, 0.05) !important;
          border-color: rgba(0, 123, 255, 0.3) !important;
          box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
          transform: translateY(-2px);
        }

        .formula-variable-item code {
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

export default FormulaHelpGuide;
